import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useGoogleLoginCallback } from 'hooks/useGoogleLoginCallback';
import { setUserAuthStatusAction } from 'store/auth-reducer/auth.reducer';
import {
  selectEmailVerifyFetchStatus,
  selectRegistrationFetchStatus,
} from 'store/auth-reducer/auth.selectors';
import {
  requestEmailResendThunkAction,
  requestEmailVerifyThunkAction,
  requestLogoutThunkAction,
  requestRegisterThunkAction,
} from 'store/auth-reducer/auth.thunk-actions';
import { fetchActiveTrialMinersAction } from 'store/purchase-reducer/purchase.thunk-actions';
import { useAppDispatch } from 'store/store';
import { StorageKey } from 'types/storage/storage-key.enum';
import { IAuthFormValues } from 'types/ui/AuthForm/auth-form-values.interface';
import { ICodeVerificationFormValues } from 'types/ui/CodeVerificationForm/code-verification-form-values.interface';
import { IOtpFormValues } from 'types/ui/OtpForm/otp-form-values.interface';

import { useCart } from 'components/logic/Cart/hooks/useCart';
import { AuthForm } from 'components/ui/AuthForm';
import { AuthSuccess } from 'components/ui/AuthSuccess';
import { TAuthSuccessVariant } from 'components/ui/AuthSuccess/types/auth-success-variant.type';
import { CodeVerificationForm } from 'components/ui/CodeVerificationForm';
import { OtpForm } from 'components/ui/OtpForm';
import { IS_TRIAL_USER, TRIAL_LINK_FROM } from 'utils/constants/trial-localstorage';
import {
  gtmClickGoToDashboardAfterEmailSubscriptionTrial,
  gtmClickSendForm,
} from 'utils/gtmSender/gtmSender';
import { setEmailSendTime } from 'utils/storage/date-and-time/set-email-send-time.util';
import { deleteEmail } from 'utils/storage/email/delete-email.util';
import { getEmail } from 'utils/storage/email/get-email.util';
import { setEmail } from 'utils/storage/email/set-email.util';

import { useRegistrationStep } from './hooks/useRegistrationStep';
import { TRegistrationStep } from './types/registration-step.type';

export const PageRegistration: FC = () => {
  const dispatch = useAppDispatch();
  const emailVerifyFetchStatus = useSelector(selectEmailVerifyFetchStatus);
  const registrationFetchStatus = useSelector(selectRegistrationFetchStatus);

  const mfaErrorHandlerRef = useRef<AsyncGenerator<string | undefined, void, string> | null>(null);

  const [registrationStep, setRegistrationStep] = useState<TRegistrationStep>('initial');

  useRegistrationStep(registrationStep, setRegistrationStep);

  const { handleTrialCheckoutClick } = useCart();

  const handleGoogleLoginClick = useGoogleLoginCallback(mfaErrorHandlerRef);
  const { t } = useTranslation();

  const handleAuthFormSubmit = ({ email, password, agreement }: IAuthFormValues) => {
    const outerRoute = localStorage.getItem(StorageKey.OuterRoute);
    let affiliateCode = undefined;
    const hasAffiliateCode = Boolean(outerRoute?.match(/^\/r\/./)?.[0]);
    if (outerRoute && hasAffiliateCode) {
      affiliateCode = outerRoute.replace(/^\/r\//, '');
    }

    setEmail(email);
    void dispatch(
      requestRegisterThunkAction({
        email,
        password,
        confirm_password: password,
        user_agree: agreement,
        affiliate_code: affiliateCode,
      }),
    );
  };

  const handleOtpFormSubmit = ({ code }: IOtpFormValues) => {
    void dispatch(
      requestEmailVerifyThunkAction({
        email: getEmail(),
        verification_code: code,
        gtmClickSendForm,
      }),
    );
  };

  const handleResendOtp = () => {
    setEmailSendTime();
    void dispatch(requestEmailResendThunkAction({ email: getEmail() }));
  };

  const handleOtpBackClick = () => void dispatch(requestLogoutThunkAction());

  const handleCodeVerificationFormSubmit = (values: ICodeVerificationFormValues) => {
    void mfaErrorHandlerRef.current?.next(Object.values(values).join(''));
  };

  const handleCodeVerificationBackClick = () => dispatch(setUserAuthStatusAction('unauthorized'));

  const handleSuccessContinue = (variant: TAuthSuccessVariant) => {
    if (variant === 'registration') {
      const isTrialUser = localStorage.getItem(IS_TRIAL_USER);
      const trialLinkPage = localStorage.getItem(TRIAL_LINK_FROM);
      if (isTrialUser) {
        trialLinkPage && gtmClickGoToDashboardAfterEmailSubscriptionTrial(trialLinkPage);

        localStorage.removeItem(IS_TRIAL_USER);
        localStorage.removeItem(TRIAL_LINK_FROM);

        dispatch(setUserAuthStatusAction('authorized-verified'));
        deleteEmail();
        handleTrialCheckoutClick();
        return;
      }
    }

    dispatch(setUserAuthStatusAction('authorized-verified'));
    deleteEmail();
  };

  useEffect(() => {
    void dispatch(fetchActiveTrialMinersAction());
  }, [dispatch]);

  switch (registrationStep) {
    case 'initial':
      return (
        <AuthForm
          type="registration"
          handleFormSubmit={handleAuthFormSubmit}
          handleGoogleLoginClick={handleGoogleLoginClick}
          isSubmitting={registrationFetchStatus === 'pending'}
          hasBackendError={registrationFetchStatus === 'rejected'}
        />
      );
    case 'email-confirm':
      return (
        <OtpForm
          title={t('registration.emailConfirmTitle')}
          description={t('registration.description')}
          email={getEmail()}
          handleFormSubmit={handleOtpFormSubmit}
          handleResendCode={handleResendOtp}
          handleBackClick={handleOtpBackClick}
          isSubmitting={emailVerifyFetchStatus === 'pending'}
          hasBackendError={emailVerifyFetchStatus === 'rejected'}
          t={t}
        />
      );
    case '2fa-required':
      return (
        <CodeVerificationForm
          title={t('registration.2faTitle')}
          handleSubmit={handleCodeVerificationFormSubmit}
          handleBackClick={handleCodeVerificationBackClick}
          t={t}
        />
      );
    case 'success':
      return (
        <AuthSuccess
          variant="registration"
          isTrial={!!(localStorage.getItem(IS_TRIAL_USER) && localStorage.getItem(TRIAL_LINK_FROM))}
          handleContinue={handleSuccessContinue}
        />
      );
  }
};
